import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { JsonCommandOk, JsonFailure } from '@skynet/data-ws-core';
import { registerErrorMessage, WS_OK_COMMAND, parseResponse } from '@skynet/data-ws-core';
import { createApiKey, createSubAccount, updateLabelApiKey } from './actions';
import { actionOnMessage } from '@skynet/data-ws-mm';
import type { ApiKeysState, OpenModalType } from './types';
import { ApiCommands } from './types';

const describeFailure = (json: JsonFailure) =>
  typeof json.message === 'string' && json.message !== ''
    ? json.message
    : 'Sorry, an unexpected error has occurred';

registerErrorMessage(ApiCommands.CreateAPIKey, {
  title: 'Create API Key Error',
  getDescription: describeFailure,
});

registerErrorMessage(ApiCommands.UpdateAPIKey, {
  title: 'Update API Key Error',
  getDescription: describeFailure,
});

registerErrorMessage(ApiCommands.CreateSubAccount, {
  title: 'Add Sub Account Error',
  getDescription: describeFailure,
});

export const initialState: ApiKeysState = {
  isOpenModal: false,
  modalInitialValue: undefined,
  typeModal: undefined,
  selectedExchange: undefined,

  reqApiKey: null,
  error: null,
};

/**
 * Drives the create/edit API key and add sub-account modals used by the
 * sub-accounts page. The page's data and error notifications live in the
 * `subAccounts` slice; this one tracks the modals' state, their submit
 * progress and their failures.
 */
const apiKeysSlice = createSlice({
  name: 'apiKeys',
  initialState,
  reducers: {
    openModal: (state, action: PayloadAction<OpenModalType>) => {
      const { type, initialValues } = action.payload;
      state.isOpenModal = true;
      state.reqApiKey = null;
      state.typeModal = type;
      state.modalInitialValue = initialValues;
      // A prefilled create form opens on its exchange, so its key fields show at
      // once. The other forms have no exchange picker, so nothing may linger.
      state.selectedExchange = type === 'new' ? initialValues?.exchange : undefined;
    },
    closeModal: (state) => {
      state.isOpenModal = false;
      state.modalInitialValue = undefined;
      state.selectedExchange = undefined;
      state.typeModal = undefined;
    },
    onSelectedExchange: (state, action: PayloadAction<number>) => {
      state.selectedExchange = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(createApiKey.pending, (state) => {
      state.reqApiKey = 'pending';
    });

    builder.addCase(updateLabelApiKey.pending, (state) => {
      state.reqApiKey = 'pending';
    });

    builder.addCase(createSubAccount.pending, (state) => {
      state.reqApiKey = 'pending';
    });

    builder.addCase(actionOnMessage, (state, action) => {
      const { json, skipProcessing, error, errorCommand } = parseResponse<{
        command: JsonCommandOk;
        okCommand: ApiCommands;
        result: string;
      }>(action.payload, Object.values(ApiCommands));

      if (skipProcessing || !json) return;

      // Matched on the error command, which is set whether or not a message is
      // registered for it: the request must be released either way, so the
      // modal can be resubmitted.
      if (errorCommand !== undefined) {
        if (error) state.error = error;
        if (state.reqApiKey === 'pending') state.reqApiKey = 'error';
        return;
      }

      if (json.command !== WS_OK_COMMAND) return;

      switch (json.okCommand) {
        case ApiCommands.CreateAPIKey:
        case ApiCommands.UpdateAPIKey:
        case ApiCommands.CreateSubAccount:
          state.reqApiKey = 'success';
          state.modalInitialValue = undefined;
          state.isOpenModal = false;
          state.selectedExchange = undefined;
          break;
      }
    });
  },
});

export const apiKeysAction: typeof apiKeysSlice.actions & {
  createApiKey: typeof createApiKey;
  updateLabelApiKey: typeof updateLabelApiKey;
  createSubAccount: typeof createSubAccount;
} = {
  ...apiKeysSlice.actions,
  createApiKey,
  updateLabelApiKey,
  createSubAccount,
};

export default apiKeysSlice.reducer;
